// dprkRussia.js — Scenario 1: DPRK troop deployment to Russia's Kursk Oblast (2024-2025).
//
// Illustrative OSINT-style reconstruction for demoing MapEditorModal's multidate capability —
// approximate dates/positions drawn from widely-reported open-source coverage (South Korean
// NIS/MND briefings, ISW, Reuters/AP wire reporting) of DPRK units training in Russia's Far
// East and later committing to combat in the Kursk salient. NOT derived from classified or
// precise geolocated sourcing — see .scratch/map-editor-demo/PLAN.md for citations/caveats.

import { buildScenarioDoc, makeLayer, makeTrack, dtg } from '../buildScenarioDoc';
import { sidc, AFFILIATION, SYMBOL_SET, TACTICAL_SIDC } from '../sidc';

const RU_DPRK = sidc(AFFILIATION.HOSTILE, SYMBOL_SET.LAND_UNIT); // combined RU/DPRK ground force (red)
const RU_DPRK_EQUIP = sidc(AFFILIATION.HOSTILE, SYMBOL_SET.LAND_EQUIPMENT);
const UA = sidc(AFFILIATION.FRIEND, SYMBOL_SET.LAND_UNIT); // Ukrainian ground force (blue)
const INSTALLATION = sidc(AFFILIATION.HOSTILE, SYMBOL_SET.LAND_INSTALLATION);

const T = {
    ukrIncursion: dtg(2024, 8, 6),
    trainingReported: dtg(2024, 10, 8),
    railMovement: dtg(2024, 10, 23),
    kurskStaging: dtg(2024, 11, 4),
    firstCombat: dtg(2024, 11, 18),
    heavyEngagement: dtg(2024, 12, 27),
    casualtiesReported: dtg(2025, 1, 13),
    sudzhaOffensive: dtg(2025, 3, 8),
    salientCleared: dtg(2025, 4, 26),
};

export const META = {
    id: 'dprk-russia',
    title: 'DPRK Troop Deployment — Kursk Oblast (2024–2025)',
    summary: 'DPRK infantry brigades train in Russia\u2019s Far East, rail-transit to Kursk Oblast, and join Russian forces in reducing Ukraine\u2019s Kursk salient.',
    dtgStartMs: T.ukrIncursion,
    dtgEndMs: T.salientCleared,
    initialDtgMs: T.firstCombat,
    center: [36.0, 51.4],
    zoom: 7,
};

const layerDprk = makeLayer({ label: 'DPRK / RU Forces' });
const layerUa = makeLayer({ label: 'Ukrainian Forces' });
const layerGraphics = makeLayer({ label: 'Control Measures' });
const layerInstallations = makeLayer({ label: 'Staging & Rear Areas' });

const tracks = [
    // -- DPRK unit: Ussuriysk training ground -> rail to Kursk -> committed to combat --------
    makeTrack({
        layerId: layerDprk.layerId,
        elementType: 'unit-icon',
        mobility: 'moving',
        snapshots: [
            [T.trainingReported, { type: 'Point', coordinates: [131.96, 43.80] }, {
                sidc: RU_DPRK, label: '11th Corps (Storm Corps) elements',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'DPRK 11th Corps', M_HIGHER_FORMATION: 'KPA General Staff', AS_COUNTRY: 'KOR (North)' },
            }],
            [T.railMovement, { type: 'Point', coordinates: [39.20, 51.66] }, {
                sidc: RU_DPRK, label: '11th Corps elements — rail transit',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'DPRK 11th Corps', M_HIGHER_FORMATION: 'KPA General Staff', AS_COUNTRY: 'KOR (North)' },
            }],
            [T.kurskStaging, { type: 'Point', coordinates: [36.30, 51.50] }, {
                sidc: RU_DPRK, label: '11th Corps elements — Kursk staging area',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'DPRK 11th Corps', M_HIGHER_FORMATION: 'KPA General Staff', AS_COUNTRY: 'KOR (North)' },
            }],
            [T.firstCombat, { type: 'Point', coordinates: [35.55, 51.28] }, {
                sidc: RU_DPRK, label: '11th Corps elements — committed, Kursk salient',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'DPRK 11th Corps', M_HIGHER_FORMATION: 'KPA General Staff', AS_COUNTRY: 'KOR (North)', K_COMBAT_EFFECTIVENESS: 'FULL' },
            }],
            [T.heavyEngagement, { type: 'Point', coordinates: [35.35, 51.23] }, {
                sidc: RU_DPRK, label: '11th Corps elements — Novoivanovka axis',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'DPRK 11th Corps', M_HIGHER_FORMATION: 'KPA General Staff', AS_COUNTRY: 'KOR (North)', K_COMBAT_EFFECTIVENESS: 'MARGINAL' },
            }],
            [T.casualtiesReported, { type: 'Point', coordinates: [35.32, 51.21] }, {
                sidc: RU_DPRK, label: '11th Corps elements — reconstituting after losses',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'DPRK 11th Corps', M_HIGHER_FORMATION: 'KPA General Staff', AS_COUNTRY: 'KOR (North)', K_COMBAT_EFFECTIVENESS: 'MARGINAL' },
            }],
        ],
    }),
    // -- Russian VDV/mech regiment providing armor support alongside DPRK infantry -----------
    makeTrack({
        layerId: layerDprk.layerId,
        elementType: 'unit-icon',
        mobility: 'moving',
        snapshots: [
            [T.kurskStaging, { type: 'Point', coordinates: [36.55, 51.58] }, {
                sidc: RU_DPRK_EQUIP, label: '810th Naval Infantry Bde (supporting armor)',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '810th NIB', AS_COUNTRY: 'RUS' },
            }],
            [T.firstCombat, { type: 'Point', coordinates: [35.75, 51.32] }, {
                sidc: RU_DPRK_EQUIP, label: '810th Naval Infantry Bde — Sudzha axis',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '810th NIB', AS_COUNTRY: 'RUS' },
            }],
            [T.sudzhaOffensive, { type: 'Point', coordinates: [35.30, 51.21] }, {
                sidc: RU_DPRK_EQUIP, label: '810th Naval Infantry Bde — Sudzha outskirts',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '810th NIB', AS_COUNTRY: 'RUS' },
            }],
        ],
    }),
    // -- Ukrainian mechanized brigade holding, then withdrawing from, the Kursk salient -----
    makeTrack({
        layerId: layerUa.layerId,
        elementType: 'unit-icon',
        mobility: 'moving',
        snapshots: [
            [T.ukrIncursion, { type: 'Point', coordinates: [35.27, 51.20] }, {
                sidc: UA, label: '22nd Mechanized Bde — Sudzha bridgehead',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '22 Mech Bde', AS_COUNTRY: 'UKR', K_COMBAT_EFFECTIVENESS: 'FULL' },
            }],
            [T.firstCombat, { type: 'Point', coordinates: [35.30, 51.22] }, {
                sidc: UA, label: '22nd Mechanized Bde — holding salient',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '22 Mech Bde', AS_COUNTRY: 'UKR', K_COMBAT_EFFECTIVENESS: 'FULL' },
            }],
            [T.heavyEngagement, { type: 'Point', coordinates: [35.33, 51.24] }, {
                sidc: UA, label: '22nd Mechanized Bde — under pressure',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '22 Mech Bde', AS_COUNTRY: 'UKR', K_COMBAT_EFFECTIVENESS: 'MARGINAL' },
            }],
            [T.sudzhaOffensive, { type: 'Point', coordinates: [35.40, 51.30] }, {
                sidc: UA, label: '22nd Mechanized Bde — withdrawing toward border',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '22 Mech Bde', AS_COUNTRY: 'UKR', K_COMBAT_EFFECTIVENESS: 'MARGINAL' },
            }],
            [T.salientCleared, { type: 'Point', coordinates: [35.38, 51.55] }, {
                sidc: UA, label: '22nd Mechanized Bde — back across the border',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '22 Mech Bde', AS_COUNTRY: 'UKR', K_COMBAT_EFFECTIVENESS: 'FULL' },
            }],
        ],
    }),
    // -- Rear-area staging installations (stationary) ----------------------------------------
    makeTrack({
        layerId: layerInstallations.layerId,
        elementType: 'unit-icon',
        mobility: 'stationary',
        snapshots: [
            [T.trainingReported, { type: 'Point', coordinates: [131.96, 43.80] }, {
                sidc: INSTALLATION, label: 'Sergeyevka training ground (DPRK instruction reported)',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Sergeyevka TG', AS_COUNTRY: 'RUS' },
            }],
        ],
    }),
    makeTrack({
        layerId: layerInstallations.layerId,
        elementType: 'unit-icon',
        mobility: 'stationary',
        snapshots: [
            [T.kurskStaging, { type: 'Point', coordinates: [36.19, 51.73] }, {
                sidc: INSTALLATION, label: 'Kursk city — combined staging & logistics hub',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Kursk staging hub', AS_COUNTRY: 'RUS' },
            }],
        ],
    }),
    // -- FLOT (control measure) tracing the Kursk salient perimeter as it is reduced ---------
    makeTrack({
        layerId: layerGraphics.layerId,
        elementType: 'tactical-line',
        mobility: null,
        graphicType: 'feba',
        snapshots: [
            [T.ukrIncursion, {
                type: 'LineString',
                coordinates: [[35.05, 51.35], [35.27, 51.20], [35.55, 51.12], [35.90, 51.10]],
            }, { sidc: TACTICAL_SIDC.FLOT, label: 'FLOT — salient at max extent' }],
            [T.firstCombat, {
                type: 'LineString',
                coordinates: [[35.10, 51.33], [35.30, 51.22], [35.60, 51.15], [35.90, 51.10]],
            }, { sidc: TACTICAL_SIDC.FLOT, label: 'FLOT — RU/DPRK counterattack begins' }],
            [T.sudzhaOffensive, {
                type: 'LineString',
                coordinates: [[35.20, 51.30], [35.32, 51.24], [35.55, 51.16], [35.85, 51.10]],
            }, { sidc: TACTICAL_SIDC.FLOT, label: 'FLOT — salient compressed' }],
            [T.salientCleared, {
                type: 'LineString',
                coordinates: [[35.30, 51.55], [35.35, 51.50], [35.45, 51.45], [35.60, 51.40]],
            }, { sidc: TACTICAL_SIDC.FLOT, label: 'FLOT — restored to pre-Aug-2024 border' }],
        ],
    }),
    // -- Direction-of-attack arrow for the DPRK/RU counter-offensive -------------------------
    makeTrack({
        layerId: layerGraphics.layerId,
        elementType: 'tactical-line',
        mobility: null,
        graphicType: 'axis-of-advance',
        snapshots: [
            [T.firstCombat, {
                type: 'LineString',
                coordinates: [[36.30, 51.50], [35.75, 51.30]],
            }, { sidc: TACTICAL_SIDC.DIRATK_GROUND, label: 'Main effort — Sudzha axis' }],
            [T.sudzhaOffensive, {
                type: 'LineString',
                coordinates: [[35.90, 51.32], [35.30, 51.21]],
            }, { sidc: TACTICAL_SIDC.DIRATK_GROUND, label: 'Main effort — Sudzha outskirts' }],
        ],
    }),
];

export const doc = buildScenarioDoc({
    dtgStartMs: META.dtgStartMs,
    dtgEndMs: META.dtgEndMs,
    layers: [layerDprk, layerUa, layerGraphics, layerInstallations],
    tracks,
    views: [{ name: 'Kursk salient', bbox: [[34.8, 51.0], [36.8, 51.9]] }],
});
